'use client'
import React, { useState } from 'react'
import Heading from '~/app/components/atoms/Heading'
import Icon from '~/app/components/atoms/Icons'
import Paragraph from '~/app/components/atoms/Paragraph'
import { motion, AnimatePresence } from 'motion/react'
import Link from 'next/link'
import Button from '~/app/components/atoms/Button'

type AccordionProps = {
  title: string
  text: any
  blocks: any
  url?: string
  label?: string
  key: number
  blank?: boolean | any
  variant?: any
  data?: any
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  text,
  url,
  label,
  variant,
  ...props
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen)
  }

  // Conditional border color logic
  const borderColorClass =
    variant.design?.color?.color === 'lys'
      ? 'border-b-rødtekst'
      : variant.design?.color?.color === 'hvid'
        ? 'border-b-rødtekst'
        : 'border-b-lys/60'

  // Conditional border color logic
  const textColorClass =
    variant.design?.color?.color === 'lys'
      ? 'text-dark'
      : variant.design?.color?.color === 'hvid'
        ? 'text-dark'
        : 'text-lys'

  // Conditional border color logic
  const fillColorClass =
    variant.design?.color?.color === 'lys'
      ? 'fill-rødtekst'
      : variant.design?.color?.color === 'hvid'
        ? 'fill-rødtekst'
        : 'fill-lys'

  return (
    <>
      <div
        {...props}
        data-type="accordion"
        className={`relative ${borderColorClass} ${textColorClass} lead border-b overflow-hidden transition-all group `}
      >
        <AccordionButton onClick={togglePanel}>
          <Heading
            className={`m-0 mr-4 text-left z-20 transition-all leading-normal w-full`}
            spaceing="none"
            tag="h5"
            type="h5"
            dangerouslySetInnerHTML={{ __html: title }}
            /
          >
          <AccordionIcon variant={variant} isPanelOpen={isPanelOpen} />
        </AccordionButton>
        <AnimatePresence>
          {isPanelOpen && (
            <motion.div
              className="z-20"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: [0.76, 0, 0.24, 1] }}
            >
              <AccordionPanel>
                <Paragraph
                  spacing="none"
                  portableText
                  size="regular"
                  className={`${textColorClass}`}
                >
                  {text}
                </Paragraph>
                {label && (
                  <div className="z-20 pt-6">
                    <Link
                      className="inline-flex w-auto gap-4 text-current group/link"
                      title={label}
                      href={url || ''}
                    >
                      {label}
                      <span className="relative z-20 overflow-hidden size-5 ">
                        <Icon
                          type="plus"
                          className="absolute transition-all duration-500 ease-in-out translate-x-1/2 -translate-y-1/2 fill-current group-hover/link:translate-x-1/2 top-1/2 group-hover/link:-right-1/2 right-1/2 size-5"
                        />
                        <Icon
                          type="plus"
                          className={`absolute transition-all duration-500 ease-in-out -translate-x-1/2 -translate-y-1/2 fill-current group-hover/link:translate-x-1/2 top-1/2 right-1/2 size-5`}
                        />
                      </span>
                    </Link>
                  </div>
                )}
              </AccordionPanel>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

const AccordionButton: React.FC<{
  onClick: () => void
  children: React.ReactNode
}> = ({ onClick, children }) => {
  return (
    <button
      className="relative flex items-center justify-between w-full py-4 transition-colors md:py-6  group-hover:text-[#8A372A]  group-hover:prose-p:text-[#8A372A] group-hover:prose-headings:text-[#8A372A]"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

const AccordionIcon: React.FC<{ isPanelOpen: boolean; variant: any }> = ({
  isPanelOpen,
  variant,
}) => {
  // Conditional border color logic
  const fillColorClass =
    variant.design?.color?.color === 'lys'
      ? 'fill-rødtekst'
      : variant.design?.color?.color === 'hvid'
        ? 'fill-rødtekst'
        : 'fill-lys'
  // Add your AccordionIcon implementation here
  return (
    <div className="flex items-center justify-center w-8 h-8">
      <svg
        className={`transition-all duration-500 ease-in-out size-6 relative ${fillColorClass} group-hover:*:fill-[#8A372A]`}
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.784 0.0218841C11.58 0.225948 9.6187 0.843036 7.73624 1.9247C5.70299 3.09299 4.00603 4.68627 2.73323 6.62192C0.810702 9.54572 -0.00388972 13.0917 0.441838 16.5968C0.918862 20.3483 2.81592 23.7209 5.79256 26.1094C8.11195 27.9706 10.9632 29.1007 13.9247 29.3325C14.4603 29.3745 15.6067 29.3739 16.1536 29.3314C20.6288 28.9838 24.7776 26.5173 27.2746 22.7198C29.3622 19.5451 30.1328 15.6204 29.4131 11.8288C29.1088 10.2254 28.4608 8.53468 27.6151 7.13811C25.2303 3.19952 21.218 0.62358 16.6 0.066396C16.0984 0.00585201 14.2692 -0.0230439 13.784 0.0218841ZM14.616 1.99404C12.76 2.04985 11.0272 2.49833 9.32059 3.36454C8.01969 4.02483 7.02062 4.74297 6.04337 5.72019C4.21054 7.55302 2.95355 9.97344 2.51845 12.5075C2.03301 15.3349 2.50501 18.2704 3.83665 20.7058C4.4491 21.8258 5.14939 22.7557 6.05928 23.6571C7.47896 25.0635 9.2467 26.1335 11.2007 26.7692C13.3202 27.4587 15.7877 27.5467 17.96 27.01C19.4927 26.6315 21.0487 25.9155 22.36 24.9856C25.2754 22.9183 27.2144 19.6041 27.5928 16.042C27.8686 13.4448 27.3667 10.8486 26.1634 8.64784C25.5509 7.52784 24.8506 6.59792 23.9408 5.69657C22.9459 4.71104 21.8963 3.967 20.584 3.31718C19.2242 2.64377 17.9736 2.2648 16.52 2.08563C16.1465 2.03961 15.0806 1.96764 14.952 1.9798C14.9168 1.98313 14.7656 1.98953 14.616 1.99404Z"
        />
        <path
          className="absolute translate-x-0 top-1/2 right-1/2 -translate-y-0"
          d="M9.91458 14.1507C9.84507 13.7108 10.0589 13.2995 10.4617 13.0982L10.6582 13H15H19.3417L19.5382 13.0982C19.8422 13.2501 20.0338 13.5076 20.0853 13.8333C20.1548 14.2732 19.941 14.6845 19.5382 14.8858L19.3417 14.984H15H10.6582L10.4617 14.8858C10.1577 14.7339 9.9661 14.4764 9.91458 14.1507Z"
        />
        <path
          className={`absolute top-1/2 right-1/2 ${isPanelOpen ? 'rotate-90 -translate-x-px -translate-y-px' : ' -translate-x-0 -translate-y-0 rotate-0'} transition-transform duration-150 ease-in-out origin-center`}
          d="M14.8413 9.01297C15.2811 8.94346 15.6925 9.15729 15.8938 9.56007L15.9919 9.75655L15.9919 14.0983L15.9919 18.4401L15.8938 18.6366C15.7418 18.9406 15.4844 19.1322 15.1586 19.1837C14.7188 19.2532 14.3074 19.0394 14.1061 18.6366L14.0079 18.4401L14.0079 14.0983L14.0079 9.75655L14.1061 9.56007C14.2581 9.25607 14.5155 9.06449 14.8413 9.01297Z"
        />
      </svg>
    </div>
  )
}

const AccordionPanel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="pr-10 md:pr-20 xl:pr-28 pb-8 prose-li:list-image-arrow prose-li:ml-8 prose-li:mb-3 text-current accordion__content has-text-wrap [&:not(:first-child)]:mr-4">
      {children}
    </div>
  )
}

export default Accordion
